import { HttpInterceptor, HttpClient, HttpHandler, HttpEvent, HttpErrorResponse, HttpInterceptorFn, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, retryWhen, tap } from 'rxjs/operators';
import { GlobalsService, JobActivityService, UtilsService } from "@services"
import { HttpErrorDialogService } from "@services/utils/http-error-dialog.service";
import { CustomErrorDialogService } from "@services/utils/custom-error-dialog.service";
import moment from "moment-timezone";
import { inject } from '@angular/core';
import { CustomErrorsStore, TimeZoneStore } from "@stores";
class ExceptionLogModel {
	exceptionLogId: number;
	userName: string;
	application: string;
	dateTime: string;
	url: string;
	statusCode?: number;
	message: string;
	stackTrace: string;
	jsonModel: string;
	fullErrorObj: string;
}

// Background convenience saves. Failing one doesn't cost the user anything they can see, so it's logged to the
// console instead of stopping them with the full-screen "ruh roh!" dialog.
const silentErrorUrls = [
	"/dashboard/updateDashboardLayout",
	// Fire-and-forget cleanup when the AI search chat closes (× or the idle auto-close). The conversation memory
	// self-expires server-side anyway, so a failure here is invisible — and the idle close fires long after the
	// user walked away, which made the dialog pop up over whatever page they were on.
	"/globalSearch/aiClearSession"
];

export const jobsightInterceptor: HttpInterceptorFn = (request, next) => {
	const customErrorsStore = inject(CustomErrorsStore, { optional: true });

	const timeZoneMap: Record<string, string> = {
		"Alaskan Standard Time": "America/Anchorage",
		"Atlantic Standard Time": "America/Halifax",
		"Central Standard Time": "America/Chicago",
		"Eastern Standard Time": "America/New_York",
		"Hawaiian Standard Time": "Pacific/Honolulu",
		"Mountain Standard Time": "America/Denver",
		"US Mountain Standard Time": "America/Phoenix",
		"Pacific Standard Time": "America/Los_Angeles",
		"W. Australia Standard Time": "Australia/Perth",
		"Cen. Australia Standard Time": "Australia/Adelaide",
		"E. Australia Standard Time": "Australia/Sydney",
	};

	// A straight-to-blob-storage request goes out as is. Only the path counts: an API call carrying a blob URL
	// in its query string (a Front of Structure imageUrl, say) still needs the API host and the auth header.
	if (request.url.split('?')[0].indexOf('jobsightdocuments') >= 0) {
		return next(request);
	}

	// Make sure the url doesn't already have the apiUrl as part of it
	let newUrl = request.url.replace(GlobalsService.apiUrl, '');
	// We need to add the prefix depending on if we're dev or prod
	newUrl = (newUrl.indexOf('/') === 0) ? (GlobalsService.apiUrl + newUrl) : (GlobalsService.apiUrl + '/' + newUrl);
	if (GlobalsService.isDebug) {
		if (newUrl.indexOf("?") > 0)
			newUrl = `${newUrl}&${new Date().getTime()}`;
		else
			newUrl = `${newUrl}?${new Date().getTime()}`;
	}

	let headers;

	const timeZoneId = GlobalsService.corporateTimeZone || "Mountain Standard Time";

	const ianaZone = timeZoneId ? timeZoneMap[timeZoneId] : 'UTC';
	const timeZoneOffset = GlobalsService.isCorporate || !ianaZone
		? moment().tz(ianaZone).utcOffset() / 60
		: moment().utcOffset() / 60;

	if (request.headers.has("Content-Type") && request.headers.get("Content-Type") === "multipart/form-data") {
		headers = request.headers
			.set("Authorization", (GlobalsService.jwtToken) ? ("Bearer " + GlobalsService.jwtToken) : "")
			.delete('Content-Type')
			.set("TimeZoneOffset", (timeZoneOffset).toString());
	} else {
		headers = request.headers
			.set("Authorization", (GlobalsService.jwtToken) ? ("Bearer " + GlobalsService.jwtToken) : "")
			.set("Content-Type", "application/json")
			.set("TimeZoneOffset", (timeZoneOffset).toString());
	}

	// Clone the request the caller made rather than building a fresh HttpRequest. Rebuilding one dropped
	// everything that isn't method/url/body/params -- responseType most of all, because HttpRequest reads
	// its options off the third argument for bodyless methods and a bare "blob" string has no
	// responseType on it. Every blob download therefore came back as "Http failure during parsing",
	// Angular having tried to read a zip or a PDF as JSON.
	const cloneReq = request.clone({ url: newUrl, headers });
	return next(cloneReq).pipe(
		// The jobs this request reset Last Activity on - the jobs grid/kanban refresh those badges.
		tap(event => {
			if (event instanceof HttpResponse) JobActivityService.publishFromHeader(event.headers.get(JobActivityService.HEADER_NAME));
		}),
		catchError((error: HttpErrorResponse) => {
			// Expected business-rule violation — let the caller's catch surface the message as a toast. No error
			// dialog, no exception log.
			if (error.status === 422)
				return throwError(() => error);

			if (error.status === 0) {
				return of();
			} else {
				console.error(error);
				const internalError = error.error || {};
				const message = <string>internalError.innerException?.exceptionMessage || <string>internalError.exceptionMessage || JSON.stringify(internalError);

				if (message === `{"isTrusted":true}`)
					return of();

				if (JSON.stringify(message).indexOf('Company has been deactivated') > 0) {
					GlobalsService.jwtToken = null;
					GlobalsService.isLoggedIn = false;
					sessionStorage.clear();
					localStorage.removeItem("jwt_token");
					alert("This company has been deactivated");
					window.location.href = "/";
					return of();
				}

				if (JSON.stringify(message).indexOf('Account has been deactivated') > 0) {
					GlobalsService.jwtToken = null;
					GlobalsService.isLoggedIn = false;
					sessionStorage.clear();
					localStorage.removeItem("jwt_token");
					alert("This account has been deactivated.  Contact your administrator.");
					window.location.href = "/";
					return of();
				}

				if (error.statusText == 'Unknown Error' ||
					message.startsWith("An exception has been raised that is likely due to a transient failure.") === true ||
					message.indexOf("was deadlocked on lock resources with another process and has been chosen as the deadlock victim. Rerun the transaction.") > 0) {
					return of({
						error: true,
						status: error.status,
						message: error.message
					} as any);
				}

				if (silentErrorUrls.some(silentUrl => request.url.toLowerCase().indexOf(silentUrl.toLowerCase()) >= 0))
					return of();

				// If saving to the exception log failed, don't try again!
				if (request.url.indexOf("saveExceptionLog") < 0) {
					try {
						const body = JSON.stringify(request.body);
						const url = request.urlWithParams;
						const exceptionLogModel = new ExceptionLogModel();
						if (GlobalsService.userInfo)
							exceptionLogModel.userName = GlobalsService.userInfo.fullName;
						exceptionLogModel.application = "JobSight Desktop";
						exceptionLogModel.url = url;
						exceptionLogModel.statusCode = error.status;
						exceptionLogModel.message = message;
						exceptionLogModel.stackTrace = internalError.stackTrace || "";
						exceptionLogModel.jsonModel = body;
						exceptionLogModel.fullErrorObj = JSON.stringify(error);

						//this.httpClient.post("/exceptionLogs/saveExceptionLog", exceptionLogModel).toPromise();

						const customMatch = customErrorsStore?.findMatch(message);
						if (customMatch && CustomErrorDialogService.show(customMatch))
							return of();

						if (HttpErrorDialogService.errorDialogComponentCallback)
							(<any>HttpErrorDialogService.errorDialogComponentCallback).showHttpError(error, url, body);

					}
					catch { }

					return of();
				}
			}

			return of();
		})
	);

}